import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-hot-toast";
import { BiLoaderCircle } from "react-icons/bi";
import { FiUploadCloud } from "react-icons/fi";
import { baseURL, privateClient } from "../configs/axiosConfig";
import heic2any from "heic2any";
import imageCompression from "browser-image-compression"; // 👈 add this

const Uploader = ({ refreshFunction, previewImage }) => {
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [imagePreview, setImagePreview] = useState("");
  const [avatar, setAvatar] = useState(null);

  useEffect(() => {
    if (previewImage) {
      setImagePreview(previewImage);
    }
  }, [previewImage]);

  // 🔑 Convert HEIC to JPEG
  const convertHeicToJpeg = async (file) => {
    try {
      const blob = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.9,
      });
      return new File([blob], file.name.replace(/\.heic|\.heif/i, ".jpg"), {
        type: "image/jpeg",
      });
    } catch (err) {
      console.error("HEIC conversion failed:", err);
      toast.error("Failed to convert HEIC image");
      return null;
    }
  };

  // 🔑 Compress to under 300 KB
  const compressImage = async (file) => {
    try {
      const options = {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
      };
      // After compression
      let compressedFile = await imageCompression(file, options);

      // Force JPEG if missing or invalid type
      if (!compressedFile.type || !compressedFile.type.startsWith("image/")) {
        compressedFile = new File([compressedFile], `${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
      }

      if (compressedFile.size > 300 * 1024) {
        toast.error("File too large even after compression!");
        return null;
      }
      return compressedFile;
    } catch (err) {
      console.error("Compression error:", err);
      toast.error("Failed to compress image");
      return null;
    }
  };

  const uploadS3Image = async (file) => {
    if (!file) return;

    if (file.type === "image/heic" || file.type === "image/heif") {
      file = await convertHeicToJpeg(file);
      if (!file) return; // stop if conversion failed
    }

    // Compress
    file = await compressImage(file);
    if (!file) return;

    if (
      !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
        file.type
      )
    ) {
      return toast.error("Invalid format. Only jpg, png, webp allowed.");
    }

    const formData = new FormData();
    formData.append("file", file);

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const res = await privateClient.post(
        "/products/uploadImageS3", // 👈 baseURL + /api already in client
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percent);
            }
          },
        }
      );

      setIsUploading(false);

      if (res.data.success) {
        setImagePreview(res.data.location); // URL from S3 response
        setAvatar(file);
        refreshFunction && refreshFunction(res.data.location);
        toast.success("Photo uploaded successfully");
      } else {
        toast.error("Failed to upload image");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Something went wrong");
      setIsUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    // if (!file) return;

    if (!file) {
      toast.error("No file picked!");
      return;
    }

    setAvatar(file);
    uploadS3Image(file);
  };

  // upload file
  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    setAvatar(file);
    uploadS3Image(file); // 👈 reuse your uploader
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    onDrop,
  });

  return (
    <div className="w-full text-center grid grid-cols-12 gap-0 flex items-center">
      <div className="lg:col-span-4 sm:col-span-4 col-span-5">
        {isUploading ? (
          // <div className="relative w-full bg-gray-300 h-4 rounded-md overflow-hidden">
          //   <div
          //     className="h-full bg-redlight transition-all"
          //     style={{ width: `${uploadProgress}%` }}
          //   />
          //   <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-white">
          //     {uploadProgress}%
          //   </span>
          // </div>
          <div className="flex flex-col items-center justify-center h-36 gap-0 text-redlight">
            <p className="text-sm font-medium text-textGray">
              {uploadProgress}%
            </p>

            <div className="w-full bg-gray-300 h-1 rounded-md overflow-hidden px-3">
              <div
                className="h-full bg-redlight transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        ) : (
          <img
            src={imagePreview || "/images/lokalai_icon.png"}
            alt="preview"
            className=" w-36 h-36 rounded-xl object-cover p-3 m-auto"
          />
        )}
      </div>

      <div
        {...getRootProps()}
        className="px-6 lg:col-span-8 sm:col-span-8 col-span-7 pt-0 pb-6 border-2 border-redlight border-dashed rounded-md cursor-pointer"
      >
        {" "}
        <label className="cursor-pointer px-4 py-2 text-white rounded-md transition duration-300 ease-in-out">
          <span className="mx-auto flex justify-center">
            <FiUploadCloud className="text-2xl text-redlight" />
          </span>

          <input
            type="file"
            name="photo"
            onChange={handleFileChange}
            className="hidden"
          />
          <p className="text-sm mt-2">Drag image here</p>
          <em className="text-xs text-gray-400">
            (Only images will be accepted)
          </em>
        </label>
      </div>
      {/* image preview */}
    </div>
  );
};

export default Uploader;
